import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ViewChild,
  HostListener,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthenticationService } from '../../../services/authentication.service';
import { CommentsService } from '../../../services/comments.service';
import { UrlService } from '../../../services/url.service';
import { Employee } from '../../../models/employee';
import 'quill-mention';
import { QuillEditorComponent } from 'ngx-quill';
import { EmployeeViewModel } from '../../../viewModels/employeeViewModel';
import { MentionItem } from '../../../models/mentions/MentionItem';
import { Constants } from 'src/app/modules/shared/models/constants';
import { T } from 'src/assets/i18n/translation-keys';
import { RichTextEditorService } from '../../../services/richTextEditor.service';
@Component({
    selector: 'app-rich-text-editor-v2',
    templateUrl: './rich-text-editor-v2.component.html',
    styleUrls: ['./rich-text-editor-v2.component.scss'],
    standalone: false
})
export class RichTextEditorV2Component implements OnInit, OnDestroy, OnChanges {
  @ViewChild(QuillEditorComponent, { static: false }) editor: QuillEditorComponent;

  @Input() placeholder = 'Write a response...';
  @Input() inputText = '';
  @Input() customMentions: MentionItem[];
  @Input() enableShortcutSave = true;
  @Input() useMentions = true;
  @Input() showProfileImage = true;
  @Input() disabled = false;
  @Input() showClear = true;
  @Input() showCancel = true;
  @Input() showUndoAndRedo = true;
  @Input() autoFocus = false;
  @Input() showCommentAlert = true;
  @Input() minLength = 3;
  @Input() maxLength = Constants.richTextMaxLength;
  @Input() enableCntrEnterSave = true;
  @Input() enableClickAwaySave = true;
  @Input() objectId: number;
  @Input() ariaLabel: string;

  @Output() saved: EventEmitter<string> = new EventEmitter<string>();
  @Output() editorChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() textChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() canceled: EventEmitter<void> = new EventEmitter<void>();
  @Output() isInvalid: EventEmitter<boolean> = new EventEmitter<boolean>();

  private subscriptions: Subscription[] = [];
  private employeeModels: EmployeeViewModel[] = [];
  private employeeIdsToMention: number[] = [];
  private mentionedUserIds: number[] = [];
  private initialInputValue = '';
  protected isContentOverLimit: boolean = false;
  public showDescriptionErrorMessage: boolean = false;

  public isMobile = false;
  public employee: Employee;
  public commentModel: string;
  public modules: any;
  public isFocused = false;
  public allowedFormats = [
    'background',
    'bold',
    'color',
    'font',
    'code',
    'italic',
    'link',
    'size',
    'strike',
    'script',
    'underline',
    'blockquote',
    'header',
    'indent',
    'list',
    'align',
    'direction',
    'code-block',
    'formula',
    // 'image'
    // 'video'
    'mention',
    'list',
  ];
  public readonly T = T;

  constructor(
    private authenticationService: AuthenticationService,
    private changeDetectorRef: ChangeDetectorRef,
    private commentsService: CommentsService,
    private urlService: UrlService,
    private richTextEditorService: RichTextEditorService,
  ) {}

  ngOnInit(): void {
    this.initialInputValue = this.inputText;
    this.commentModel = this.inputText;
    this.employee = this.authenticationService.getCurrentEmployee();

    if (this.useMentions && this.employee) {
      this.getFeed();
    } else {
      this.loadConfiguration();
      this.changeDetectorRef.markForCheck();
    }

    this.checkIsMobile();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.inputText) {
      this.commentModel = this.inputText;
    }

    if (changes.inputText?.currentValue === "") {
      this.showDescriptionErrorMessage = false;
      this.isInvalid.emit(false);
    }
  }


  get disableSaveButton() {
    if (this.commentModel) {
      const strippedText = this.stripHtml(this.commentModel).trim();
      return strippedText.length < this.minLength || strippedText.length > this.maxLength;
    }

    return true;
  }

  public save() {
    this.saved.next(this.commentModel);
  }

  public saveOnCtrlEnter() {
    if (this.enableCntrEnterSave) {
      this.save();
    }
  }

  public saveOnClickAway() {
    if (this.enableClickAwaySave) {
      this.save();
    }
  }

  public onNgModelChange(text: string) {
    if (!text) {
      this.textChanged.next('');
      return;
    }

    if (this.stripHtml(text).length > this.maxLength) {
      this.enableClickAwaySave = false;
      this.enableCntrEnterSave = false;
      this.isContentOverLimit = true;
      this.isInvalid.emit(true);
      this.showDescriptionErrorMessage = true;
      this.changeDetectorRef.markForCheck();
    } else {
      this.enableClickAwaySave = true;
      this.enableCntrEnterSave = true;
      this.isContentOverLimit = false;
      this.isInvalid.emit(false);
      this.showDescriptionErrorMessage = false;
      this.changeDetectorRef.markForCheck();
      this.textChanged.next(text);
    }
  }

  public getAvatarUrl(thumbnailUrl: string): string {
    return this.urlService.buildResourceUrl(thumbnailUrl);
  }

  public onEditorFocused(ev: any) {
    if (ev && ev.editor) {
      this.isFocused = true;

      ev.editor.container.classList.add('focus');
      ev.editor.container.nextElementSibling.classList.add('focus');

      // complains if the any is removed as the type becomes 'unknown'
      const editor: any = this.editor.quillEditor.getModule('mention');

      const mentionContainer = editor.mentionContainer;
      mentionContainer.style.height = '300px';
      mentionContainer.style.overflowY = 'scroll';
    }
  }

  public onEditorBlur(ev: any) {
    if (!this.commentModel) {
      this.isContentOverLimit = false;
      this.showDescriptionErrorMessage = false;
      this.isInvalid.emit(false);
      return;
    }

    if (ev && ev.editor) {
      this.isFocused = false;
      ev.editor.container.classList.remove('focus');
      ev.editor.container.nextElementSibling.classList.remove('focus');

      if (this.commentModel !== this.initialInputValue && this.stripHtml(this.commentModel).length <= this.maxLength) {
        this.save();
        return;
      } else if (this.stripHtml(this.commentModel).length <= this.maxLength){
        this.canceled.next();
      }
    }
  }

  public changedEditor(ev: any) {
    if (!this.useMentions && ev && ev.text) {
      const value = ev.html;
      this.editorChanged.next(value);
    }

    if (this.objectId) {
      this.richTextEditorService.setCurrentEdit({
        id: this.objectId,
        content: this.commentModel,
      });
    }
  }

  public cancel() {
    this.commentModel = undefined;
    this.changeDetectorRef.markForCheck();
    this.canceled.next();
  }

  public clear() {
    this.commentModel = undefined;
    this.changeDetectorRef.markForCheck();
  }

  public setFocus(editor: any) {
    if (this.ariaLabel && editor) {
      const editorElement = editor.root;
      if (editorElement && this.ariaLabel) {
        editorElement.setAttribute('aria-label', this.ariaLabel);
        // Remove the content from screen reader by setting aria-describedby to empty
        // This makes the screen reader only announce the label, not the content
        editorElement.setAttribute('role', 'textbox');
        editorElement.setAttribute('aria-multiline', 'true');
      }

      if (this.autoFocus) {
        editor.focus();
      }
    }
  }

  public redo() {
    this.editor.quillEditor.history.redo();
  }

  public undo() {
    this.editor.quillEditor.history.undo();
  }

  private getFeed() {
    this.subscriptions.push(
      this.commentsService.getAllEmployeesByAccountId().subscribe((res) => {
        this.employeeModels = res;

        this.employeeModels.forEach((m) => {
          m.thumbnailURL = this.urlService.buildResourceUrl(m.thumbnailURL);
        });
        this.loadConfiguration();
        this.changeDetectorRef.markForCheck();
      })
    );
  }

  private loadConfiguration(): void {
    const modules = {
      mention: {
        onSelect: (item, insertItem) => {
          insertItem(item);
          this.mentionedUserIds.push(item.id);
        },
        mentionDenotationChars: ['@', '#'],
        source: (searchTerm: string, renderList, mentionChar) => {
          const values: {id: number, value: string, avatarHTML: string}[] = [];

          if (this.useMentions) {
            if (mentionChar === '@') {
              this.employeeModels.forEach((m) =>
                values.push({
                  id: m.id,
                  value: `${m.firstName} ${m.surname}`,
                  avatarHTML: m.thumbnailURL,
                })
              );
            } else if (mentionChar === '#' && this.customMentions) {
              this.customMentions.forEach((m) => {
                values.push({ id: m.id, value: `${m.salutation}`, avatarHTML: '' });
              });
            }

            if (searchTerm.length === 0) {
              renderList(values, searchTerm);
            } else {
              const matches = [];
              values.forEach((entry) => {
                if (entry.value.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1) {
                  matches.push(entry);
                }
              });
              renderList(matches, searchTerm);
            }
          }
        },
      },
    };

    this.modules = modules;
  }

  private checkIsMobile(): void {
    this.isMobile = window.innerWidth <= Constants.xs;
  }

  private stripHtml(text: string): string {
    const regex = /(<([^>]*)>)/gi;

    return text?.replace(regex, '').trim();
  }
}
